import {
  Button,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import { ExternalRoutes } from '@spaceship-fspl/helpers';
import {
  backgroundColor,
  getSpace,
  match,
  minHeight,
  paddingBottom,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { HeaderProps } from 'components';
import aaaBadgeImg from 'images/spaceship-super_aaa-rating-badge.svg';
import moneyMagBadgeImg from 'images/spaceship-super_money-mag-badge.svg';
import React from 'react';
import styled from 'styled-components';

const HeroPanel = styled.div<{ backgroundImageURL: string }>`
  ${minHeight({ xs: '774px', md: '660px', lg: '800px' })}
  ${backgroundColor('neutral.100')}
  ${paddingBottom({ xs: 'lg', sm: 'none', md: 'md', xl: 'none' })}

  ${match('md')<{ backgroundImageURL: string }>`
    min-height: 660px;
    background-image: url(${({ backgroundImageURL }) => backgroundImageURL});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: bottom;
  `};

  ${match('lg')`
    min-height: 800px;
    background-position: center;
  `};
`;

const StyledColumns = styled(Columns)`
  & > div {
    ${match('md')`
      flex-direction: row-reverse;
    `}
  }
`;

const StyledContainer = styled(Container)`
  position: relative;
  ${paddingBottom({ xs: 'none', sm: 'lg', md: 'none' })};
`;

const BadgeContainer = styled(Columns)`
  ${match('md')`
    position: absolute;
    left: ${getSpace('md')};
    bottom: 0;
  `};

  ${match('xl')`
    left: 0;
    bottom: -10%;
  `};
`;

const Badge = styled.img`
  width: 100px;
  height: 100px;

  ${match('md')`
    width: 140px;
    height: 140px;
  `};
`;

interface SuperHeroPanelProps {
  headerComponent: React.FC<HeaderProps>;
  backgroundImageURL: string;
  mobileIcon: React.ReactNode;
  product: string;
  title: string;
  tagline?: string | React.ReactNode;
}

export const SuperHeroPanel: React.FC<
  React.PropsWithChildren<SuperHeroPanelProps>
> = ({
  headerComponent: HeaderComponent,
  backgroundImageURL,
  mobileIcon,
  product,
  title,
  tagline,
}) => (
  <TrackingCategory category="Hero Panel">
    <HeroPanel backgroundImageURL={backgroundImageURL}>
      <Stack spaceY={{ xs: 'lg', sm: 'xxl', md: 'xl', lg: 'xxl' }}>
        <HeaderComponent buttonVariant="primary" />
        <StyledContainer>
          <Stack spaceY={{ xs: 'lg', md: 'none' }} alignX="center">
            <StyledColumns
              alignY="center"
              alignX={{ xs: 'center', md: 'left' }}
            >
              <Columns.Column width={{ xs: 1, md: 4 / 8, lg: 6 / 12 }}>
                <Stack
                  spaceY={{ xs: 'lg', md: 'md', lg: 'lg' }}
                  alignX={{ xs: 'center', md: 'left' }}
                >
                  <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                    <Stack
                      spaceY={{ xs: 'sm', md: 'md', lg: 'sm' }}
                      alignX={{ xs: 'center', md: 'left' }}
                    >
                      <Stack spaceY={{ xs: 'md', md: 'none' }}>
                        <Visible
                          isHidden={{ xs: false, md: true }}
                          displayValue="block"
                        >
                          {mobileIcon}
                        </Visible>
                        <Text
                          variant={2}
                          color="neutral.070"
                          align={{ xs: 'center', md: 'left' }}
                        >
                          {product}
                        </Text>
                      </Stack>
                      <Heading
                        variant={1}
                        color="neutral.000"
                        align={{ xs: 'center', md: 'left' }}
                        component="h1"
                      >
                        {title}
                      </Heading>
                    </Stack>
                    <Columns>
                      <Columns.Column width={{ xs: 1, md: 0.85, lg: 4 / 6 }}>
                        {tagline}
                      </Columns.Column>
                    </Columns>
                  </Stack>

                  <Button
                    size="lg"
                    variant="primary"
                    href={ExternalRoutes.SUPER_SIGNUP}
                  >
                    Sign up
                  </Button>
                </Stack>
              </Columns.Column>
            </StyledColumns>
            <BadgeContainer
              spaceX={{ xs: 'sm', md: 'none' }}
              spaceY={{ xs: 'none', md: 'sm' }}
            >
              <Columns.Column width={{ xs: 1 / 2, md: 1 }}>
                <Badge
                  src={moneyMagBadgeImg}
                  alt="Spaceship Money Magazine 2022 Winner Award"
                />
              </Columns.Column>
              <Columns.Column width={{ xs: 1 / 2, md: 1 }}>
                <Badge
                  src={aaaBadgeImg}
                  alt="Spaceship Rainmaker AAA 2021 quality rating"
                />
              </Columns.Column>
            </BadgeContainer>
          </Stack>
        </StyledContainer>
      </Stack>
    </HeroPanel>
  </TrackingCategory>
);
